import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "divide-y divide-gray-200 rounded-lg bg-white shadow" }
const _hoisted_2 = { class: "px-4 py-5 sm:px-6 flex items-center gap-x-4" }
const _hoisted_3 = {
  type: "button",
  class: "inline-flex items-center gap-x-2 rounded-md bg-white px-2.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
}
const _hoisted_4 = { class: "h-5 w-5 shrink-0 text-gray-900 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold leading-6 text-gray-900" }, " Add Student "),
  /*#__PURE__*/_createElementVNode("p", { class: "mt-1 text-sm text-gray-500" }, " Set up an account for a Student so they can log in ")
], -1)
const _hoisted_6 = { class: "px-4 py-5 sm:px-6" }
const _hoisted_7 = { class: "grid grid-cols-2 gap-y-2.5 gap-x-4" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("label", {
  for: "firstName",
  class: "formkit-label text-slate-700 text-sm font-medium"
}, [
  /*#__PURE__*/_createTextVNode(" First Name"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-base text-red-500" }, "*")
], -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("label", {
  for: "lastName",
  class: "formkit-label text-slate-700 text-sm font-medium"
}, [
  /*#__PURE__*/_createTextVNode(" Surname"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-base text-red-500" }, "*")
], -1)
const _hoisted_10 = {
  for: "email",
  class: "formkit-label text-slate-700 text-sm font-medium"
}
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "text-base text-red-500" }, "*", -1)
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("label", {
  for: "grade",
  class: "formkit-label text-slate-700 text-sm font-medium"
}, [
  /*#__PURE__*/_createTextVNode(" Grade"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-base text-red-500" }, "*")
], -1)
const _hoisted_15 = { class: "col-span-2 flex gap-x-4" }
const _hoisted_16 = {
  key: 0,
  class: "formkit-messages mb-0"
}
const _hoisted_17 = {
  class: "formkit-message text-red-500 text-xs font-inter font-normal",
  id: "lastName-rule_required",
  "data-message-type": "validation"
}
const _hoisted_18 = { class: "px-4 py-4 sm:px-6 flex items-center gap-x-2 justify-end" }
const _hoisted_19 = { class: "w-full flex items-center gap-x-2" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = {
  key: 0,
  class: "flex items-center justify-center gap-x-2"
}
const _hoisted_22 = {
  key: 1,
  class: "font-semibold"
}

import {  ref, watch, computed } from 'vue';
import { FormKitNode, submitForm } from "@formkit/core";
import { useLearnerStore } from '@/stores/learnerStore';
import { useSchoolStore } from '@/stores/schoolStore';

import showPasswordIcon from "@/assets/auth-module/show-password.svg";
import hidePasswordIcon from "@/assets/auth-module/hide-password.svg";
import { showToast } from '@/utils/toastUtils';
import { shouldHideSeniorPrimaryV2 } from '@/utils/assessment-availability';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

interface NewLearnerData {
  email: string;
  password: string;
  lastName: string;
  firstName: string;
  studentNumber: string | "";

  grade: number;
  schoolId: string;
  mathsClass: string;
  englishClass: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddLearnerModalComponent',
  emits: ["closeModal"],
  setup(__props, { emit }) {



// Stores for managing state
const schoolStore = useSchoolStore();
const learnerStore = useLearnerStore();

const passwordVisible = ref(false);
const loading = ref(false);
const classErrorMessage = ref();
const newLearner = ref<NewLearnerData>({
  email: "",
  password: "",
  lastName: "",
  firstName: "",
  studentNumber: "",
  
  mathsClass: "",
  englishClass: "",

  schoolId: "",
  grade: schoolStore.currentSchoolStructure?.gradeLevels[0] || 3
});

// Computed property to get the password icon
const getPasswordIcon = computed(() => {
  return passwordVisible.value ? showPasswordIcon : hidePasswordIcon;
});

const showGenerateEmail = computed(() => {
  const lastNameAdded = newLearner.value.lastName && newLearner.value.lastName.length > 0;
  const firstNameAdded = newLearner.value.firstName && newLearner.value.firstName.length > 0;
  return firstNameAdded && lastNameAdded;
})

// Check if subjects are available
const isMathsAvailable = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.subjectsAvailable) return false;
  return structure.subjectsAvailable.some(subject => subject.code === 'MATHS');
});

const isEnglishAvailable = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.subjectsAvailable) return false;
  return structure.subjectsAvailable.some(subject => subject.code === 'ENGLISH');
});

// Check if school has only one subject (for simplified labels)
const isSingleSubjectSchool = computed(() => {
  return (isMathsAvailable.value && !isEnglishAvailable.value) ||
         (!isMathsAvailable.value && isEnglishAvailable.value);
});

// Dynamic labels based on whether school has single or multiple subjects
const mathsClassLabel = computed(() => isSingleSubjectSchool.value ? 'Class' : 'Maths Class');
const englishClassLabel = computed(() => isSingleSubjectSchool.value ? 'Class' : 'English Class');
const mathsClassPlaceholder = computed(() => isSingleSubjectSchool.value ? 'Select Class' : 'Select Maths Class');
const englishClassPlaceholder = computed(() => isSingleSubjectSchool.value ? 'Select Class' : 'Select English Class');

// Get the subject IDs
const mathsSubjectId = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.subjectsAvailable) return null;
  const mathsSubject = structure.subjectsAvailable.find(subject => subject.code === 'MATHS');
  return mathsSubject?._id || null;
});

const englishSubjectId = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.subjectsAvailable) return null;
  const englishSubject = structure.subjectsAvailable.find(subject => subject.code === 'ENGLISH');
  return englishSubject?._id || null;
});

// Grade options based on school structure
const gradeOptions = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.gradeLevels) return [];
  
  const grades = structure.gradeLevels.map(grade => ({
    name: `Grade ${grade}`,
    id: grade
  }));

  return grades;
});

// Maths class options filtered by selected grade
const mathsClassOptionsByGrade = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.classGroups || !mathsSubjectId.value) return [];

  const mathsClasses = structure.classGroups
    .filter(classGroup =>
      classGroup.subject === mathsSubjectId.value &&
      classGroup.gradeLevel === newLearner.value.grade
    )
    .map(classGroup => ({
      name: `${classGroup.name} (Grade ${classGroup.gradeLevel})`,
      id: classGroup._id
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Add empty option at the beginning
  const emptyOptionLabel = isSingleSubjectSchool.value ? 'Select Class' : 'Select Maths Class';
  return [{ name: emptyOptionLabel, id: '' }, ...mathsClasses];
});

// English class options filtered by selected grade
const englishClassOptionsByGrade = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.classGroups || !englishSubjectId.value) return [];

  const englishClasses = structure.classGroups
    .filter(classGroup =>
      classGroup.subject === englishSubjectId.value &&
      classGroup.gradeLevel === newLearner.value.grade
    )
    .map(classGroup => ({
      name: `${classGroup.name} (Grade ${classGroup.gradeLevel})`,
      id: classGroup._id
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Add empty option at the beginning
  const emptyOptionLabel = isSingleSubjectSchool.value ? 'Select Class' : 'Select English Class';
  return [{ name: emptyOptionLabel, id: '' }, ...englishClasses];
});

// Maths class options - all grades, sorted by grade level
const mathsClassOptions = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.classGroups || !mathsSubjectId.value) return [];

  const mathsClasses = structure.classGroups
    .filter(classGroup => classGroup.subject === mathsSubjectId.value)
    .map(classGroup => ({
      name: `${classGroup.name} (Grade ${classGroup.gradeLevel})`,
      id: classGroup._id,
      gradeLevel: classGroup.gradeLevel
    }))
    .sort((a, b) => {
      // First sort by grade level
      if (a.gradeLevel !== b.gradeLevel) {
        return a.gradeLevel - b.gradeLevel;
      }
      // Then sort by class name within the same grade
      return a.name.localeCompare(b.name);
    });

  // Add empty option at the beginning
  const emptyOptionLabel = isSingleSubjectSchool.value ? 'Select Class' : 'Select Maths Class';
  return [{ name: emptyOptionLabel, id: '' }, ...mathsClasses];
});

// English class options - all grades, sorted by grade level
const englishClassOptions = computed(() => {
  const structure = schoolStore.currentSchoolStructure;
  if (!structure?.classGroups || !englishSubjectId.value) return [];

  const englishClasses = structure.classGroups
    .filter(classGroup => classGroup.subject === englishSubjectId.value)
    .map(classGroup => ({
      name: `${classGroup.name} (Grade ${classGroup.gradeLevel})`,
      id: classGroup._id,
      gradeLevel: classGroup.gradeLevel
    }))
    .sort((a, b) => {
      // First sort by grade level
      if (a.gradeLevel !== b.gradeLevel) {
        return a.gradeLevel - b.gradeLevel;
      }
      // Then sort by class name within the same grade
      return a.name.localeCompare(b.name);
    });

  // Add empty option at the beginning
  const emptyOptionLabel = isSingleSubjectSchool.value ? 'Select Class' : 'Select English Class';
  return [{ name: emptyOptionLabel, id: '' }, ...englishClasses];
});

// When the chosen school/class doesn't support SPV2, send an explicit
// assessmentType override so the API skips its grade-5–7 SPV2 default.
const mathsAssessmentTypeOverride = computed(() => {
  const grade = newLearner.value.grade;
  if (!newLearner.value.mathsClass) return null;
  if (grade == null || grade < 5 || grade > 7) return null;
  const hide = shouldHideSeniorPrimaryV2({
    schoolLocale: schoolStore.currentSchoolStructure?.preferredLang,
    schoolGroupId: schoolStore.currentSchoolStructure?.schoolGroup,
  });
  return hide ? 'MATHS_SENIOR_PRIMARY' : null;
});

// Reset class selections when grade changes
watch(() => newLearner.value.grade, () => {
  newLearner.value.mathsClass = '';
  newLearner.value.englishClass = '';
});

// Watch for firstName changes to auto-generate password
watch(() => newLearner.value.firstName, (newFirstName: string) => {
  if (newFirstName && newFirstName.length >= 3) {
    generatePassword();
  }
});

const togglePasswordVisibility = (value: string) => {
  if (value == "password") {
    passwordVisible.value = !passwordVisible.value;
  }
};

// Generate password for the new learner
const generatePassword = () => {
  const password = newLearner.value.firstName.slice(0, 3) + "123";
  newLearner.value.password = password.toLowerCase();
};

const generateEmail = () => {
  const email = `${newLearner.value.firstName.toLowerCase()}.${newLearner.value.lastName.toLowerCase()}@${
    schoolStore.getCurrentSchool?.domain
  }`;
  newLearner.value.email = email;
};

const email_exists = async (node: FormKitNode): Promise<boolean> => {
  try {
    const emailExists = await learnerStore.validateEmail(node.value as string);
    return !emailExists;
  } catch (error) {
    return true;
  }
};

const triggerFormSubmit = async () => {
  if (learnerStore.loader.learner || loading.value) return;
  submitForm('add-new-learner-form')
}

const onSubmitAddLearnerForm = async () => {
  if (learnerStore.loader.learner || loading.value) return;
  if (!validateEitherClassField()) return;
  try {
    loading.value = true;
    // Collect selected classes
    const classes = [];
    const subjects = [];
    
    if (isMathsAvailable.value && newLearner.value.mathsClass) {
      classes.push(newLearner.value.mathsClass);
      subjects.push('MATHS');
    }
    
    if (isEnglishAvailable.value && newLearner.value.englishClass) {
      classes.push(newLearner.value.englishClass);
      subjects.push('ENGLISH');
    }

    // Validate that at least one class is selected
    if (classes.length === 0) {
      showToast({
        text: 'Please select at least one class',
        type: 'error',
        icon: CheckCircleIcon,
      });
      return;
    }

    // Create the learner payload
    const learnerPayload = {
      classes: classes,
      subjects: subjects,
      email: newLearner.value.email,
      grade: newLearner.value.grade,
      password: newLearner.value.password,
      lastName: newLearner.value.lastName,
      firstName: newLearner.value.firstName,
      schoolId: schoolStore.currentSchool?._id,
      studentNumber: newLearner.value.studentNumber || '',
      ...(mathsAssessmentTypeOverride.value && { assessmentType: mathsAssessmentTypeOverride.value }),
    };

    // Create the learner
    const result = await learnerStore.createLearner(learnerPayload);
    
    if (result) {
      showToast({
        text: `Successfully added ${newLearner.value.firstName} ${newLearner.value.lastName}`,
        type: 'success',
        icon: CheckCircleIcon,
      });
      
      emit('closeModal');
    }
  } catch (error) {
    console.error('Failed to create learner:', error);
    showToast({
      text: 'Failed to add student. Please try again.',
      type: 'error',
      icon: CheckCircleIcon,
    });
  } finally {
    loading.value = false;
  }
};

const validateEitherClassField = () => {
  const mathsExists = isMathsAvailable.value;
  const englishExists = isEnglishAvailable.value;
  const maths = mathsExists ? newLearner.value.mathsClass : null;
  const english = englishExists ? newLearner.value.englishClass : null;

  if (!mathsExists && !englishExists) {
    classErrorMessage.value = '';
    return true;
  }

  if (mathsExists && englishExists) {
    if (!maths && !english) {
      classErrorMessage.value = 'Either Maths or English class is required.';
      return false;
    }
  }

  // If only maths exists, require it
  if (mathsExists && !englishExists) {
    if (!maths) {
      classErrorMessage.value = 'Maths class is required.';
      return false;
    }
  }

  // If only english exists, require it
  if (!mathsExists && englishExists) {
    if (!english) {
      classErrorMessage.value = 'English class is required.';
      return false;
    }
  }

  classErrorMessage.value = '';
  return true;
}


return (_ctx: any,_cache: any) => {
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _component_FormKit = _resolveComponent("FormKit")!
  const _component_spinner_loader = _resolveComponent("spinner-loader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, [
          _createVNode(_component_rl_icon, {
            icon: 'rl-add-user',
            title: 'rl chat bubbles',
            "aria-hidden": "true"
          })
        ])
      ]),
      _hoisted_5
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_component_FormKit, {
        type: "form",
        actions: false,
        modelValue: newLearner.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newLearner).value = $event)),
        id: "add-new-learner-form",
        onSubmit: onSubmitAddLearnerForm
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_FormKit, {
              type: "text",
              label: `First Name`,
              id: "firstName",
              name: "firstName",
              "outer-class": "input-thin",
              placeholder: 
              `First Name`
            ,
              validation: "required",
              classes: {
              outer: 'col-span-1',
            }
            }, {
              label: _withCtx(() => [
                _hoisted_8
              ]),
              suffix: _withCtx(({ state: { valid, validationVisible } }) => [
                (validationVisible && !valid)
                  ? (_openBlock(), _createBlock(_unref(InformationCircleIcon), {
                      key: 0,
                      class: "h-4 w-4 mr-2 absolute right-0 top-1/2 -translate-y-1/2 text-red-500"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_FormKit, {
              type: "text",
              label: `Surname`,
              id: "lastName",
              name: "lastName",
              "outer-class": "input-thin",
              placeholder: 
              `Surname`
            ,
              validation: "required",
              suffixIcon: false,
              classes: {
              outer: 'col-span-1',
            }
            }, {
              label: _withCtx(() => [
                _hoisted_9
              ]),
              suffix: _withCtx(({ state: { valid, validationVisible } }) => [
                (validationVisible && !valid)
                  ? (_openBlock(), _createBlock(_unref(InformationCircleIcon), {
                      key: 0,
                      class: "h-4 w-4 mr-2 absolute right-0 top-1/2 -translate-y-1/2 text-red-500"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_FormKit, {
              type: "email",
              name: "email",
              id: "email",
              placeholder: 
              `johndoe@school.edu`
            ,
              validation: "email|required|length:0,60|(500)email_exists",
              "validation-rules": { email_exists: email_exists },
              "validation-messages": {
              email_exists: `There's already another Student with the same email address`,
              length: `Email must be 60 characters or fewer`,
            },
              classes: {
              outer: 'col-span-1'
            }
            }, {
              label: _withCtx(() => [
                _createElementVNode("label", _hoisted_10, [
                  _createTextVNode(" Email (Username)"),
                  _hoisted_11,
                  _createElementVNode("span", null, [
                    _createElementVNode("button", {
                      type: "button",
                      disabled: !showGenerateEmail.value,
                      class: "ml-2 text-blue-500 disabled:opacity-50",
                      onClick: generateEmail
                    }, [
                      (newLearner.value.email.length > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, "Re-"))
                        : _createCommentVNode("", true),
                      _createTextVNode("Generate Username ")
                    ], 8, _hoisted_12)
                  ])
                ])
              ]),
              suffix: _withCtx(({ state: { valid, validationVisible } }) => [
                (validationVisible && !valid)
                  ? (_openBlock(), _createBlock(_unref(InformationCircleIcon), {
                      key: 0,
                      class: "h-4 w-4 mr-2 absolute right-0 top-1/2 -translate-y-1/2 text-red-500"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["placeholder", "validation-rules"]),
            _createVNode(_component_FormKit, {
              type: "text",
              name: "password",
              label: `Password`,
              "outer-class": "input-thin",
              placeholder: 
              `Password (Optional)`
            ,
              validation: "required|length:6",
              classes: {
              outer: 'col-span-1',
            }
            }, {
              suffix: _withCtx(({ state: { valid, validationVisible } }) => [
                (validationVisible && !valid)
                  ? (_openBlock(), _createBlock(_unref(InformationCircleIcon), {
                      key: 0,
                      class: "h-4 w-4 mr-2 absolute right-0 top-1/2 -translate-y-1/2 text-red-500"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["placeholder"]),
            _createVNode(_component_FormKit, {
              type: "text",
              name: "studentNumber",
              label: `Student Number`,
              "outer-class": "input-thin",
              placeholder: "Student Number (Optional)",
              classes: {
              outer: 'col-span-1',
            }
            }),
            _createVNode(_component_FormKit, {
              id: "grade",
              type: "choose",
              size: 'md',
              showIcon: false,
              name: "grade",
              label: "Grade",
              modelValue: newLearner.value.grade,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newLearner.value.grade) = $event)),
              selectOptions: gradeOptions.value,
              placeholder: 'Select Grade',
              validation: "required",
              classes: {
              outer: 'col-span-1',
            }
            }, {
              label: _withCtx(() => [
                _hoisted_14
              ]),
              _: 1
            }, 8, ["modelValue", "selectOptions"]),
            _createElementVNode("div", _hoisted_15, [
              (isMathsAvailable.value)
                ? (_openBlock(), _createBlock(_component_FormKit, {
                    key: 0,
                    type: "choose",
                    size: 'md',
                    showIcon: false,
                    name: "mathsClass",
                    label: mathsClassLabel.value,
                    "label-class": "text-gray-400",
                    modelValue: newLearner.value.mathsClass,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newLearner.value.mathsClass) = $event)),
                    placeholder: mathsClassPlaceholder.value,
                    selectOptions: mathsClassOptionsByGrade.value,
                    classes: {
                outer: 'w-full',
              }
                  }, null, 8, ["label", "modelValue", "placeholder", "selectOptions"]))
                : _createCommentVNode("", true),
              (isEnglishAvailable.value)
                ? (_openBlock(), _createBlock(_component_FormKit, {
                    key: 1,
                    type: "choose",
                    size: 'md',
                    showIcon: false,
                    name: "englishClass",
                    label: englishClassLabel.value,
                    "label-class": "text-gray-400",
                    modelValue: newLearner.value.englishClass,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newLearner.value.englishClass) = $event)),
                    placeholder: englishClassPlaceholder.value,
                    selectOptions: englishClassOptionsByGrade.value,
                    classes: {
                outer: 'w-full',
              }
                  }, null, 8, ["label", "modelValue", "placeholder", "selectOptions"]))
                : _createCommentVNode("", true)
            ]),
            (classErrorMessage.value)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_16, [
                  _createElementVNode("li", _hoisted_17, _toDisplayString(classErrorMessage.value), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_18, [
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("button", {
          onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('closeModal'))),
          type: "button",
          class: "w-1/2 rounded-md bg-white px-2.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        }, " Cancel "),
        _createElementVNode("button", {
          onClick: _cache[5] || (_cache[5] = ($event: any) => (triggerFormSubmit())),
          type: "button",
          disabled: _unref(learnerStore).loader.learner || loading.value,
          class: "w-1/2 rounded-md bg-blue-600 hover:bg-blue-500 px-2.5 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        }, [
          (_unref(learnerStore).loader.learner || loading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_21, [
                _createVNode(_component_spinner_loader, { color: 'text-white dark:text-white' }),
                _createTextVNode(" " + _toDisplayString(_unref(learnerStore).loadingMessage || 'Creating...'), 1)
              ]))
            : (_openBlock(), _createElementBlock("span", _hoisted_22, " Add Student "))
        ], 8, _hoisted_20)
      ])
    ])
  ]))
}
}

})